import { DecimalPipe } from "@angular/common";
import { ChangeDetectionStrategy, Component, inject, model, OnInit, signal } from "@angular/core";
import { FormsModule } from "@angular/forms";

import { PlanHttpService } from "@admin/components/plans/services/plan-http.service";
import { AuthService } from "@core/services/auth.service";
import { LoggerService } from "@core/services/logger.service";
import { MarkdownService } from "@features/documentation/services/markdown.service";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { CustomIconComponent } from "@shared/components/custom-icon/custom-icon.component";
import { BaseModalComponent } from "@shared/components/modal/base-modal.component";
import { ModalComponent } from "@shared/components/modal/modal.component";
import { PlanModel } from "@shared/models/plan.model";
import { PlanType } from "@shared/models/plan.model";
import { ToastService } from "@shared/services/toast.service";

import { BillingHttpService } from "../../services/billing-http.service";

@Component({
  selector: "component-choose-plan-modal",
  templateUrl: "./choose-plan-modal.component.html",
  styleUrl: "./choose-plan-modal.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DecimalPipe, ModalComponent, FormsModule, CustomIconComponent]
})
export class ChoosePlanModalComponent extends BaseModalComponent implements OnInit {
  protected readonly plans = signal<PlanModel[]>([]);
  protected readonly isLoading = signal(true);
  protected readonly isRedirecting = signal(false);
  protected readonly currentUserPlanId = signal<string | null>(null);
  protected readonly billingCycle = signal<"monthly" | "yearly">("monthly");

  protected readonly activeModal = inject(NgbActiveModal);
  protected readonly markdown = inject(MarkdownService);
  private readonly _billingHttp = inject(BillingHttpService);
  private readonly _planHttp = inject(PlanHttpService);
  private readonly _toast = inject(ToastService);
  private readonly _logger = inject(LoggerService);
  private readonly _auth = inject(AuthService);

  readonly headerTitle = model<string>("You're all signed up! Choose your plan");
  readonly headerSubtitle = model<string>("Start by selecting the plan that's right for you.");
  readonly showSkipButton = model<boolean>(true);

  ngOnInit(): void {
    this.currentUserPlanId.set(this._auth.currentUser()?.organization?.planId ?? null);

    this._planHttp.getAllPublic().subscribe({
      next: (response) => {
        this.plans.set(response.data);
        this.isLoading.set(false);
      },
      error: () => {
        this.isLoading.set(false);
        this._toast.error("Could not load plans. Please try again later.");
        this.activeModal.close();
      }
    });
  }

  onUpgrade(plan: PlanModel): void {
    // Handle free plan change
    if (plan.type === PlanType.FREE) {
      this.isRedirecting.set(true);
      this._billingHttp.changePlan(plan.id).subscribe({
        next: () => {
          this._toast.success("You have successfully changed to the Free plan.");
          this._auth.updateCurrentUser();
          this.activeModal.close();
        },
        error: (err) => {
          this._logger.error("Failed to change plan", err);
          this._toast.error("Could not change your plan. Please try again.");
          this.isRedirecting.set(false);
        }
      });
      return;
    }

    // Handle paid plan checkout
    const priceId = this.billingCycle() === "monthly" ? plan.stripeMonthlyPriceId : plan.stripeAnnualPriceId;
    if (!priceId) {
      this._toast.error("This plan does not have a price configured for the selected billing cycle.");
      return;
    }
    this.isRedirecting.set(true);
    this._billingHttp.createCheckoutSession(priceId).subscribe({
      next: (response) => {
        // Redirect to Stripe checkout
        window.location.href = response.url;
      },
      error: (err) => {
        this._logger.error("Failed to create checkout session", err);
        this._toast.error("Could not proceed to checkout. Please try again.");
        this.isRedirecting.set(false);
      }
    });
  }
}
